import React, { useState, useEffect, useRef } from 'react';
import EVisitChangeOverDialog from '../dialogs/EVisitChangeOverDialog';
import { getInlineMarkDown } from '../utils';
import '../../../stylesheets/triagebot/EvisitCard.scss';

const EvisitCard = ({ message, sendMessage, disabled, isSpanish = false }) => {

    const [selectedButton, setSelectedButton]             = useState(null);
    const [showAgreeDialog, setShowAgreeDialog]           = useState(false);
    const [pendingEvisitSymptoms, setPendingEvisitSymptoms] = useState(null);
    const [submitted, setSubmitted]                       = useState(false);
    const [isEvisitSubmitting, setIsEvisitSubmitting]     = useState(false);

    const sectionOneHeadingRef = useRef(null);

    // ── Bot data ─────────────────────────────────────────────────────────────
    const cardData      = message?.data?.[0] || {};
    const evisitCardContent    = cardData.evisitCardContent || {};
    const symptomsData  = (message?.data?.[2] && Array.isArray(message.data[2]))
        ? message.data[2].filter(item => item.cause)
        : [];

    const stripBold = (text) => text?.replace(/\*\*/g, '') || '';
    const renderBold = (text) => {
        if (!text) return '';
        // Merge adjacent bold spans separated by a hyphen: **E**-**Visit** → **E-Visit**
        const merged = text.replace(/\*\*(.*?)\*\*-(\*\*.*?\*\*)/g, (_, a, b) => `**${a}-${b.replace(/\*\*/g, '')}**`);
        return getInlineMarkDown(merged);
    };

    const sectionHeading      = stripBold(evisitCardContent.textA);
    const sectionDescription  = evisitCardContent.textB;
    const sectionOneLabel     = stripBold(evisitCardContent.textC);

    const continueHeading     = stripBold(evisitCardContent.textD);
    const continueDescription = evisitCardContent.textE;
    const continueButtonText  = stripBold(evisitCardContent.textF);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleSymptomClick = (cause, wdfId) => {
        setPendingEvisitSymptoms({ name: cause, id: String(wdfId), type: 'evisitSymptom' });
        setSelectedButton(cause);
        setShowAgreeDialog(true);
    };

    const handleAgreeDialogCancel = () => {
        setShowAgreeDialog(false);
        setIsEvisitSubmitting(false);
        setSelectedButton(null);
        setPendingEvisitSymptoms(null);
    };

    const handleEvisitAgreed = () => {
        if (isEvisitSubmitting) return;

        setIsEvisitSubmitting(true);
        setSubmitted(true);
        if (pendingEvisitSymptoms) {
            sendMessage('message', JSON.stringify([pendingEvisitSymptoms]), [pendingEvisitSymptoms]);
        }
    };

    const handleContinueSymptomSmart = () => {
        if (!disabled && !submitted) {
            setSubmitted(true);
            sendMessage('message', 'Continue', 'Continue');
        }
    };

    // Focus the first section heading when the card mounts for screen reader users.
    useEffect(() => {
        sectionOneHeadingRef.current?.focus();
    }, []);

    // Keep popup 1 open while loading; close it once popup 2 is ready.
    useEffect(() => {
        const handleConfirmed = () => {
            setShowAgreeDialog(false);
            setIsEvisitSubmitting(false);
        };
        const handleRedirected = () => {
            // Safety fallback for flows that only emit redirected.
            setShowAgreeDialog(false);
            setIsEvisitSubmitting(false);
        };

        window.addEventListener('evisit-confirmed', handleConfirmed);
        window.addEventListener('evisit-redirected', handleRedirected);
        return () => {
            window.removeEventListener('evisit-confirmed', handleConfirmed);
            window.removeEventListener('evisit-redirected', handleRedirected);
        };
    }, []);

    const isDisabled = disabled || submitted;

    return (
        <div className={`evisit-card card${isDisabled ? ' disabled' : ''}${isSpanish ? ' spanish-language' : ''}`}>
        <div className='content'>

            {/* ── Section 1: Common symptoms ── */}
            <div className='evisit-card__section evisit-card__section--symptoms'>
                <p
                    className='evisit-card__section-heading'
                    ref={sectionOneHeadingRef}
                    tabIndex='-1'
                >
                    {sectionHeading}
                </p>
                <p className='evisit-card__section-description'>{renderBold(sectionDescription)}</p>
                <p className='evisit-card__section-label'>{sectionOneLabel}</p>

                <div className='evisit-card__symptom-buttons'>
                    {symptomsData.map((symptom, index) => (
                        <button
                            key={symptom.id || index}
                            className={`evisit-card__symptom-btn${selectedButton === symptom.cause ? ' selected' : ''}`}
                            onClick={() => handleSymptomClick(symptom.cause, symptom.wdfId)}
                            disabled={isDisabled}
                        >
                            {symptom.cause}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Section 2: Continue to Symptom Smart ── */}
            <div className='evisit-card__section evisit-card__section--symptom-smart'>
                <p
                    className='evisit-card__section-heading'
                    tabIndex='0'
                >
                    {continueHeading}
                </p>
                <p className='evisit-card__section-description'>{renderBold(continueDescription)}</p>
                <button
                    className='evisit-card__continue-btn'
                    onClick={handleContinueSymptomSmart}
                    disabled={isDisabled}
                >
                    {continueButtonText}
                    <span className='evisit-card__continue-arrow'>&#8250;</span>
                </button>
            </div>

        </div>

            <EVisitChangeOverDialog
                message={message}
                mode='fastTrack'
                open={showAgreeDialog}
                onCancel={handleAgreeDialogCancel}
                onAgree={handleEvisitAgreed}
                isSubmitting={isEvisitSubmitting}
                selectedSymptom={pendingEvisitSymptoms?.name}
            />
        </div>
    );
};

export default EvisitCard;
